import { DefaultParamType, TFnType, TranslationKey, useTranslate } from '@tolgee/react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import useModeratedResponseCounts from '../../../hooks/useModeratedResponseCounts';
import useResponses from '../../../hooks/useResponses';
import { RoundType } from '../../../services/backendService/types';
import { zIndexes } from '../../../utils/zIndexes';
import AnticipationLoader from '../../_atoms/AnticipationLoader/AnticipationLoader';
import { Slide } from '../../SlideComponents/slide.types';

// Max time to show the "waiting for moderation" loader before falling back to
// the real count. Moderation is real-time (seconds), so this only guards the
// degenerate case where a response never flips to isModerated.
const MODERATION_WAIT_TIMEOUT_MS = 10000;

export const toSlideType = (
  t: TFnType<DefaultParamType, string, TranslationKey>,
  type?: string | RoundType,
  plural?: boolean,
) => {
  if (type === 'answers')
    return { count: 'words', description: t(`Word${plural ? 's written' : ' written'}`) };
  if (type === 'aiAssessedAnswers')
    return { count: 'words', description: t(`Word${plural ? 's written' : ' written'}`) };
  if (type === 'aiAssessedImageAnswers')
    return { count: 'details', description: t(`Detail${plural ? 's added' : ' added'}`) };
  if (type === 'wordcloud')
    return { count: 'words', description: t(`Word${plural ? 's added' : ' added'}`) };
  if (type === 'canvas')
    return { count: 'details', description: t(`Detail${plural ? 's added' : ' added'}`) };
  if (type === 'poll')
    return { count: 'responses', description: t(`Vote${plural ? 's' : ''}`) };
  if (type === 'voting')
    return { count: 'votes', description: `Vote${plural ? 's' : ''}` };
  return { count: '', description: '' };
};

const ActivitySpecificResponseSummary = ({
  currentSlide,
  gameId,
  tenantId,
}: {
  currentSlide: Slide;
  gameId: string;
  tenantId: string;
}) => {
  const { responses } = useResponses(gameId, currentSlide.id, true, tenantId);
  const { t } = useTranslate();

  const { moderatedResponses, wordCount, detailCount } = useModeratedResponseCounts(
    responses,
  );

  // Determine which count to display. Only words/details activities (writing
  // and drawing) go through AI text moderation, so only those gate on the
  // moderated subset. Poll/voting/multiple-choice are structured choices that
  // never get isModerated set, so they must count all (non-dismissed)
  // responses — otherwise their counter would permanently read 0.
  const slideTypeInfo = toSlideType(t, currentSlide.type, true);
  const targetCount =
    slideTypeInfo.count === 'words'
      ? wordCount
      : slideTypeInfo.count === 'details'
      ? detailCount
      : responses.length;

  // For words/details cards: if responses exist but none have been moderated
  // yet, show a loader instead of "0" so the teacher knows we're still
  // waiting on moderation rather than thinking nothing was submitted.
  const isWaitingForModerationRaw =
    (slideTypeInfo.count === 'words' || slideTypeInfo.count === 'details') &&
    responses.length > 0 &&
    moderatedResponses.length === 0;

  // Moderation has no guaranteed client-side "done" signal — a response can
  // stay unmoderated (e.g. a moderation error, or all responses get dismissed)
  // and never flip to isModerated. Bound the wait so the loader can't hang
  // forever: after the timeout we fall back to the real count (which may be 0).
  const [moderationWaitExpired, setModerationWaitExpired] = useState(false);

  useEffect(() => {
    if (!isWaitingForModerationRaw) {
      setModerationWaitExpired(false);
      return;
    }
    const timeoutId = setTimeout(
      () => setModerationWaitExpired(true),
      MODERATION_WAIT_TIMEOUT_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [isWaitingForModerationRaw]);

  const isWaitingForModeration = isWaitingForModerationRaw && !moderationWaitExpired;

  // Animation logic — always tween from the value currently on screen, so a
  // target change mid-animation continues smoothly instead of snapping back
  // to the previous start point.
  const [displayCount, setDisplayCount] = useState(0);
  const displayCountRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    displayCountRef.current = displayCount;
  }, [displayCount]);

  useEffect(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const startValue = displayCountRef.current;
    if (targetCount === startValue) return;

    if (targetCount < startValue) {
      setDisplayCount(targetCount);
      return;
    }

    const endValue = targetCount;
    const duration = 3500;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 5);
      const currentValue = Math.round(startValue + (endValue - startValue) * easeOut);
      setDisplayCount(currentValue > 0 ? currentValue : 0);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [targetCount]);

  // Get description with correct pluralization based on displayed count
  const description =
    slideTypeInfo.count === 'words'
      ? toSlideType(t, currentSlide.type, displayCount !== 1).description
      : slideTypeInfo.count === 'details'
      ? toSlideType(t, currentSlide.type, displayCount !== 1).description
      : toSlideType(t, currentSlide.type, displayCount !== 1).description;

  return (
    <FullSlideBlur>
      <ResponseCounterWidgetContainerCenter style={{ height: 'auto' }}>
        {isWaitingForModeration ? (
          <CountLoader />
        ) : (
          <p
            style={{
              width: '100%',
              fontSize: '80px',
              marginBottom: '0px',
              lineHeight: '80px',
            }}
          >
            {displayCount}
          </p>
        )}
        <div style={{ fontSize: '30px' }}>{description}</div>
      </ResponseCounterWidgetContainerCenter>
    </FullSlideBlur>
  );
};

// Keep the loader the same height as the 80px number it replaces, so the card
// doesn't resize when the count appears.
const CountLoader = styled(AnticipationLoader)`
  min-height: 80px;
`;

const ResponseCounterWidgetContainerCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  padding: 20px;
  font-weight: 600;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  right: 50%;
  border-radius: 10px;
  background-color: ${theme.colors.translucentLightBlack};
  position: absolute;
  font-size: 36px;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: center;
  top: 50%;
  transform: translateX(50%) translateY(-50%);
  z-index: ${zIndexes('RESPONSECOUNTER_WIDGET_CONTAINER_CENTER')};
  max-width: 500px;
`;

const FullSlideBlur = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background-color: ${theme.colors.lessTranslucentWhite};
  z-index: ${zIndexes('RESPONSECOUNTER_WIDGET_CONTAINER_CENTER')};
`;

export default ActivitySpecificResponseSummary;
